'use client';
import clsx from 'clsx';
import React, { isValidElement } from 'react';
import { Link } from '../../../../Buttons/Link/Link.mjs';
import { Checkbox } from '../../../../Controls/Checkbox/Checkbox.mjs';
import '../../../../Controls/Checkbox/CheckboxGroup/CheckboxGroup.mjs';
import '../../../../Controls/Checkbox/CheckboxCard/CheckboxCard.mjs';
import '../../../../Controls/Checkbox/CheckboxIndicator/CheckboxIndicator.mjs';
import '../../../../Controls/Checkbox/Checkbox.context.mjs';
import { Tag } from '../../../../DataDisplay/Tag/Tag.mjs';
import { factory } from '../../../../factory/factory.utils.mjs';
import { Typography } from '../../../../Foundations/Typography/Typography.mjs';
import { Flex } from '../../../../Layout/Flex/Flex.mjs';
import { Box } from '../../../../Other/Box/Box.mjs';
import { omitText } from '../../../../utils/helpers.mjs';
import { ColorSwatches } from '../ColorSwatches/ColorSwatches.mjs';
import classes from './ProductCardInformation.module.css.mjs';

const ProductCardInformation = factory((props, ref) => {
  const {
    title,
    description,
    tags,
    bundleItems,
    SKU,
    taxesLabel,
    checkbox,
    additionalLink,
    colorSwatches,
    outOfStockLabel,
    price,
    slashedPrice,
    ...rest
  } = props;
  const leftNodes = [];
  if (colorSwatches) leftNodes.push({ key: "colorSwatches", marginBottom: 15, node: /* @__PURE__ */ React.createElement(ColorSwatches, { ...colorSwatches }) });
  if (tags && tags.length)
    leftNodes.push({
      key: "tags",
      marginBottom: 5,
      node: /* @__PURE__ */ React.createElement(Flex, { gap: 10, wrap: "wrap" }, tags.map((tag, i) => /* @__PURE__ */ React.createElement(Tag, { key: i, ...tag })))
    });
  if (title)
    leftNodes.push({
      key: "title",
      marginBottom: 5,
      node: /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(title),
          textColor: "var(--content-secondary)",
          style: { overflowWrap: "anywhere" },
          size: "label-regular-l",
          component: title.component || "span"
        },
        title.text
      )
    });
  if (description)
    leftNodes.push({
      key: "description",
      marginBottom: 10,
      node: /* @__PURE__ */ React.createElement(
        Typography,
        {
          ...omitText(description),
          size: "paragraph-medium",
          textColor: "var(--content-tertiary)",
          component: isValidElement(description.text) ? "div" : description.component || "p"
        },
        description.text
      )
    });
  if (SKU)
    leftNodes.push({
      key: "sku",
      marginBottom: 10,
      node: /* @__PURE__ */ React.createElement(
        Typography,
        {
          component: isValidElement(SKU.text) ? "div" : SKU.component || "span",
          ...omitText(SKU),
          textColor: "var(--content-tertiary)",
          size: "label-regular-s"
        },
        SKU.text
      )
    });
  if (bundleItems)
    leftNodes.push({
      key: "bundleItems",
      marginBottom: 10,
      node: /* @__PURE__ */ React.createElement(
        Typography,
        {
          component: isValidElement(bundleItems.text) ? "div" : bundleItems.component || "span",
          ...omitText(bundleItems),
          textColor: "var(--content-tertiary)",
          size: "label-regular-s"
        },
        bundleItems.text
      )
    });
  if (price || slashedPrice || taxesLabel)
    leftNodes.push({
      key: "prices",
      marginBottom: 10,
      node: /* @__PURE__ */ React.createElement(Flex, { direction: "column", gap: 5 }, (price || slashedPrice) && /* @__PURE__ */ React.createElement(Flex, { direction: "row", wrap: "wrap", gap: 5 }, price && /* @__PURE__ */ React.createElement(
        Typography,
        {
          component: isValidElement(price.text) ? "div" : price.component || "span",
          ...omitText(price),
          textColor: "var(--content-secondary)",
          size: "label-medium-m"
        },
        price.text
      ), slashedPrice && /* @__PURE__ */ React.createElement(
        Typography,
        {
          component: isValidElement(slashedPrice.text) ? "div" : slashedPrice.component || "span",
          ...omitText(slashedPrice),
          textColor: "var(--content-tertiary)",
          size: "label-regular-m",
          textDecoration: "line-through"
        },
        slashedPrice.text
      )), taxesLabel && /* @__PURE__ */ React.createElement(
        Typography,
        {
          component: isValidElement(taxesLabel.text) ? "div" : taxesLabel.component || "span",
          ...omitText(taxesLabel),
          size: "label-medium-s",
          textColor: "var(--content-secondary)",
          textTransform: "uppercase"
        },
        taxesLabel.text
      ))
    });
  if (outOfStockLabel)
    leftNodes.push({
      key: "oos",
      marginBottom: 10,
      node: /* @__PURE__ */ React.createElement(
        Typography,
        {
          component: isValidElement(outOfStockLabel.text) ? "div" : outOfStockLabel.component || "span",
          ...omitText(outOfStockLabel),
          textColor: "var(--content-tertiary)",
          size: "paragraph-small"
        },
        outOfStockLabel.text
      )
    });
  if (additionalLink)
    leftNodes.push({
      key: "additionalLink",
      node: (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        /* @__PURE__ */ React.createElement(
          Link,
          {
            component: additionalLink.onClick ? "button" : "a",
            className: classes.productCardAdditionalLink,
            width: "fit-content",
            ...omitText(additionalLink),
            size: "small"
          },
          additionalLink.text
        )
      )
    });
  const rightNodes = [];
  if (checkbox)
    rightNodes.push({
      key: "checkbox",
      marginLeft: 15,
      marginBottom: 10,
      node: /* @__PURE__ */ React.createElement(Checkbox, { textColor: "var(--content-tertiary)", ...checkbox })
    });
  const pairCount = Math.min(leftNodes.length, rightNodes.length);
  const children = [];
  for (let i = 0; i < pairCount; i++) {
    children.push(
      /* @__PURE__ */ React.createElement(Box, { key: leftNodes[i].key, marginBottom: leftNodes[i].marginBottom, className: classes.leftPair }, leftNodes[i].node)
    );
    children.push(
      /* @__PURE__ */ React.createElement(
        Box,
        {
          key: rightNodes[i].key,
          marginLeft: rightNodes[i].marginLeft,
          marginBottom: rightNodes[i].marginBottom,
          className: clsx(classes.right, i === 0 && classes.rightFirst)
        },
        rightNodes[i].node
      )
    );
  }
  for (let i = pairCount; i < leftNodes.length; i++) {
    children.push(
      /* @__PURE__ */ React.createElement(
        Box,
        {
          key: leftNodes[i].key,
          marginBottom: i !== leftNodes.length - 1 ? leftNodes[i].marginBottom : void 0,
          className: classes.leftFull
        },
        leftNodes[i].node
      )
    );
  }
  return /* @__PURE__ */ React.createElement(Box, { ref, ...rest, className: classes.productCardInformation }, children);
});

export { ProductCardInformation };
